import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Filtros del listado — §4.1 del contrato
 * (`~/Documents/multicomercio/competencia/60-modulo-solicitudes-contrato.md`).
 *
 * 🔴 Los NOMBRES coinciden letra por letra con los query params que espera el
 * servidor: así la URL de la pantalla (que sí lleva estos filtros, ver el
 * componente de lista) se puede pegar tal cual en el body de una prueba.
 */
export interface FiltrosSolicitudes {
  cedula?: string;
  merchant_code?: string;
  shop?: string;
  cash_register?: string;
  cajero?: string;
  status?: string;
  etapa?: string;
  desde?: string;
  hasta?: string;
  credito?: string;
  factura?: string;
  page?: number;
  limit?: number;
}

/**
 * Solicitudes de crédito del canal POS — «Órdenes» en el portal del comercio,
 * la misma entidad (`SOL-MT-000123`) mirada desde la consola de CrediTotal.
 *
 * 🔴 SE MIRA, NO SE ACTÚA (alcance de esta vuelta, §3 del contrato): sólo hay
 * lectura acá. Anular, reprocesar un COBRO_INCOMPLETO o forzar un vencimiento
 * tocan plata y quedan para otra vuelta, con su propio permiso y su rastro.
 *
 * Sólo el lado ADMIN (`/admin/solicitudes`, ve todos los comercios). El lado
 * comercio (`/comercio/solicitudes`, sólo lo suyo) es del portal, no de esta
 * consola.
 */
@Injectable({ providedIn: 'root' })
export class SolicitudesService {
  private api: string;

  constructor(private httpClient: HttpClient) {
    this.api = `${environment.BASE_LA}/api/integracion-pos/admin/solicitudes`;
  }

  /**
   * §4.1 — el listado. `resumen` viaja calculado sobre el FILTRO aplicado, no
   * sobre la página que se está mirando: por eso la frase de cierre de la
   * pantalla no cambia al pasar de página, sólo al cambiar un filtro.
   */
  getSolicitudes = async (filtros: FiltrosSolicitudes = {}): Promise<any> => {
    const params: string[] = [];
    const agregar = (clave: string, valor: any): void => {
      if (valor === undefined || valor === null || valor === '') return;
      params.push(`${clave}=${encodeURIComponent(String(valor))}`);
    };
    agregar('cedula', filtros.cedula);
    agregar('merchant_code', filtros.merchant_code);
    agregar('shop', filtros.shop);
    agregar('cash_register', filtros.cash_register);
    agregar('cajero', filtros.cajero);
    agregar('status', filtros.status);
    agregar('etapa', filtros.etapa);
    agregar('desde', filtros.desde);
    agregar('hasta', filtros.hasta);
    agregar('credito', filtros.credito);
    agregar('factura', filtros.factura);
    agregar('page', filtros.page);
    agregar('limit', filtros.limit);
    const qs = params.length ? `?${params.join('&')}` : '';
    return lastValueFrom(this.httpClient.get<any>(`${this.api}${qs}`));
  };

  /**
   * §4.2 — la solicitud completa: `monto`, `initial_payment`, `cobro_attempts`,
   * `credito_real` (leído de `invoices`, no copiado), `rastro` (`{etapa, at,
   * detalle}`), `annulment` y `eventos` (el historial, `{tipo, en, actor, datos}`).
   *
   * Es la sección que costó tres hallazgos críticos en la certificación del
   * 2026-09-20: no existía en el contrato, cada front adivinó sus propios
   * nombres, y ninguno acertó. El componente de detalle lee estos nombres tal
   * cual — no una lista de candidatos — porque tantear varios (`a?.b ?? a?.c`)
   * es lo que escondió el defecto: no falla nunca, y por eso nunca avisa que
   * el nombre está mal.
   */
  getSolicitud = async (code: string): Promise<any> =>
    lastValueFrom(this.httpClient.get<any>(`${this.api}/${encodeURIComponent(code)}`));
}
